import React, {
  KeyboardEvent,
  RefObject,
  WheelEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { MarkerMargin } from './MarkerMargin';
import { MediaMargin } from './MediaMargin';
import type { MediaLabel } from '../viewModel/mediaLabels';
import type { NameGap } from '../viewModel/publicNames';
import css from './ScriptEditor.module.scss';

/** An empty line still occupies a row, in the textarea and so in the overlay. */
const BLANK_ROW = String.fromCharCode(0x200b);

/** One stable empty map, so a margin carrying only notes does not re-render on every keystroke. */
const NO_LABELS: Map<number, MediaLabel[]> = new Map();

/** The two numbers the caret point is measured with. */
export type TextMetrics = { padTop: number; lineHeight: number };

const DEFAULT_METRICS: TextMetrics = { padTop: 18, lineHeight: 24 };

/**
 * Three layers only stay on the same rows while they wrap at the same width. The textarea is the one
 * carrying a scrollbar, so its client box is the measurement the other two are handed
 * (editor.js:139-152).
 */
export function measureColumns(frame: HTMLElement | null, textarea: HTMLTextAreaElement | null): TextMetrics {
  if (!frame || !textarea || typeof getComputedStyle !== 'function') {
    return DEFAULT_METRICS;
  }
  const style = getComputedStyle(textarea);
  const inner = textarea.clientWidth
    - (parseFloat(style.paddingLeft) || 0)
    - (parseFloat(style.paddingRight) || 0);
  if (Number.isFinite(inner) && inner > 0) {
    frame.style.setProperty('--basil-text-width', `${textarea.clientWidth}px`);
    frame.style.setProperty('--basil-wrap-width', `${inner}px`);
  }
  const height = parseFloat(style.lineHeight);

  return {
    padTop: parseFloat(style.paddingTop) || 0,
    lineHeight: Number.isFinite(height) && height > 0 ? height : (parseFloat(style.fontSize) || 15) * 1.55,
  };
}

/** One line of the syntax overlay, as elements — never as markup, so every string is escaped. */
export function syntaxLine(line: string): React.ReactNode {
  if (line === '') {
    return BLANK_ROW;
  }
  if (/^\s*==/.test(line)) {
    return <span className={css.synScene}>{line}</span>;
  }
  if (/^\s*\?/.test(line)) {
    return <span className={css.synNote}>{line}</span>;
  }
  if (/^\s*>/.test(line)) {
    return <span className={css.synDirective}>{line}</span>;
  }
  if (/^\s*[*-]/.test(line)) {
    return <span className={css.synChoice}>{line}</span>;
  }
  const character = line.match(/^([A-Z][A-Z0-9 _.-]*?)(\s+\([^)]+\))?$/);
  if (character) {
    return (
      <>
        <span className={css.synName}>{character[1]}</span>
        {character[2] ? <span className={css.synMood}>{character[2]}</span> : null}
      </>
    );
  }

  return line;
}

/** What the writing surface needs. `BasilModal` owns every one of these values. */
export type ScriptEditorProps = {
  /** The script; the textarea's value and the only copy of it. */
  value: string;
  /** The script's logical lines, already split. */
  lines: string[];
  /** The lines the margin marks with a `?`. */
  markedLines: ReadonlySet<number>;
  /** The 1-based logical line the caret is on. */
  currentLine: number;
  /**
   * P1: the line "Show an example" lit, or null.
   *
   * It is the OVERLAY that is lit, never the textarea's value: the example points at a line the
   * writer already has, and nothing about it is inserted, copied or changed.
   */
  exampleLine?: number | null;
  /**
   * P3: the episode's pictures, video and sound, as read-only labels keyed by line.
   *
   * They are rendered in a margin of their own, never in the textarea's value: the grammar does not
   * change in this round, and a label is not a script line.
   */
  mediaLabels?: Map<number, MediaLabel[]>;
  /**
   * T17-06: the missing-name guidance, keyed by the line of the label it is about.
   *
   * It is rendered in the same margin as the media labels, beside the speaker line or the heading
   * it names, so a writer meets it where the thing it is about is.
   */
  nameNotes?: Map<number, NameGap[]>;
  /** Ask to open a scene from a label. The surface decides, and asks before discarding edits. */
  onOpenScene?: (branchId: number) => void;
  /** T19-04: ask for every label on a line, which the surface shows in a panel of its own. */
  onShowLine?: (line: number) => void;
  /** Read-only scripts still read, scroll and select. */
  readOnly?: boolean;
  textareaRef: RefObject<HTMLTextAreaElement>;
  /** The caret mirror, one line wide, invisible; the popover measures the caret with it. */
  mirrorRef: RefObject<HTMLDivElement>;
  onChange: (next: string) => void;
  onCaretMove: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onGoToLine: (line: number) => void;
  /** Called whenever the layers were lifted, so anything hanging off the caret follows. */
  onScrollSync?: () => void;
  /** Called with the measured metrics whenever the column is re-measured. */
  onMetrics?: (metrics: TextMetrics) => void;
  /** ARIA wiring for the autocomplete listbox. */
  listboxId: string;
  isListOpen: boolean;
  activeOptionId?: string;
};

/**
 * The bounded frame: the textarea is the ONLY scroller, and the overlay and the margin are lifted by
 * the same transform (editor.js:126-138), so neither can become a second scroll container nor
 * stretch the modal to the height of the whole script.
 */
export function ScriptEditor(props: ScriptEditorProps) {
  const {
    value,
    lines,
    markedLines,
    currentLine,
    exampleLine,
    mediaLabels,
    nameNotes,
    onOpenScene,
    onShowLine,
    readOnly,
    textareaRef,
    mirrorRef,
    onChange,
    onCaretMove,
    onKeyDown,
    onGoToLine,
    onScrollSync,
    onMetrics,
    listboxId,
    isListOpen,
    activeOptionId,
  } = props;
  const frameRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLPreElement>(null);
  const rowsRef = useRef<HTMLDivElement>(null);
  const mediaRowsRef = useRef<HTMLDivElement>(null);

  const syncScroll = useCallback(() => {
    const node = textareaRef.current;
    if (!node) {
      return;
    }
    const lift = `translateY(${-node.scrollTop}px)`;
    if (highlightRef.current) {
      highlightRef.current.style.transform = lift;
    }
    if (rowsRef.current) {
      rowsRef.current.style.transform = lift;
    }
    if (mediaRowsRef.current) {
      mediaRowsRef.current.style.transform = lift;
    }
    if (onScrollSync) {
      onScrollSync();
    }
  }, [onScrollSync, textareaRef]);

  const remeasure = useCallback(() => {
    const metrics = measureColumns(frameRef.current, textareaRef.current);
    if (onMetrics) {
      onMetrics(metrics);
    }
    syncScroll();
  }, [onMetrics, syncScroll, textareaRef]);

  useEffect(() => {
    remeasure();
    const node = textareaRef.current;
    const ResizeObserverCtor = typeof window === 'undefined' ? undefined : window.ResizeObserver;
    let observer: ResizeObserver | null = null;
    if (node && ResizeObserverCtor) {
      observer = new ResizeObserverCtor(() => remeasure());
      observer.observe(node);
    }
    const documentFonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    if (documentFonts && documentFonts.ready && typeof documentFonts.ready.then === 'function') {
      documentFonts.ready.then(() => remeasure()).catch(() => undefined);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [remeasure, textareaRef]);

  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    const node = textareaRef.current;
    if (!node) {
      return;
    }
    const before = node.scrollTop;
    node.scrollTop = before + event.deltaY;
    // The page keeps the wheel at the ends: only a scroll that actually moved is taken.
    if (node.scrollTop !== before) {
      event.preventDefault();
      syncScroll();
    }
  }, [syncScroll, textareaRef]);

  const handleScroll = useCallback(() => {
    syncScroll();
  }, [syncScroll]);

  return (
    <section className={css.editorFrameWrap}>
      <div className={css.legend} aria-hidden="true">
        <span className={css.keyScene}>scene</span>
        <span className={css.keyName}>character</span>
        <span className={css.keyChoice}>choice</span>
        <span className={css.keyDirective}>directive</span>
      </div>
      <div
        className={css.editorFrame}
        ref={frameRef}
        data-testid="basil-editor-frame"
        data-media={Boolean(mediaLabels && mediaLabels.size > 0)}
      >
        <MarkerMargin
          lines={lines}
          markedLines={markedLines}
          currentLine={currentLine}
          onGoToLine={onGoToLine}
          rowsRef={rowsRef}
          onWheel={handleWheel}
        />
        <div className={css.textStack}>
          <pre className={css.highlight} aria-hidden="true" ref={highlightRef} data-testid="basil-highlight">
            {lines.map((line, index) => {
              const number = index + 1;
              const painted = [
                number === currentLine ? css.curLine : '',
                number === exampleLine ? css.exampleLine : '',
              ].filter(Boolean).join(' ');

              return (
                <React.Fragment key={`overlay-${number}`}>
                  {index > 0 ? '\n' : null}
                  {painted ? (
                    <span
                      className={painted}
                      data-testid={number === exampleLine ? 'basil-example-line' : 'basil-current-line'}
                    >
                      {syntaxLine(line)}
                    </span>
                  ) : syntaxLine(line)}
                </React.Fragment>
              );
            })}
            {'\n'}
          </pre>
          <textarea
            className={css.textarea}
            ref={textareaRef}
            value={value}
            wrap="soft"
            spellCheck
            readOnly={readOnly}
            aria-label="Episode script"
            role="combobox"
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded={isListOpen}
            aria-activedescendant={isListOpen ? activeOptionId : undefined}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={onKeyDown}
            onKeyUp={onCaretMove}
            onClick={onCaretMove}
            onSelect={onCaretMove}
            onScroll={handleScroll}
          />
          <div className={css.caretMirror} aria-hidden="true" ref={mirrorRef} />
        </div>
        {(mediaLabels && mediaLabels.size > 0) || (nameNotes && nameNotes.size > 0) ? (
          <MediaMargin
            lines={lines}
            labelsByLine={mediaLabels ?? NO_LABELS}
            notesByLine={nameNotes}
            onOpenScene={onOpenScene}
            onShowLine={onShowLine}
            rowsRef={mediaRowsRef}
            onWheel={handleWheel}
          />
        ) : null}
      </div>
    </section>
  );
}
